import React, {useEffect} from 'react';
import {
  LeftColumnContent,
  RightColumnContent,
  TwoColumnGrid,
} from './components';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  Select,
  Switch,
  Text,
  Textarea,
} from '@chakra-ui/react';
import {ScrollableFlex, WrapperFlex} from '../../utils/scrolling';
import {AgentBuilderPageIndicator} from './AgentBuilderPageIndicator';
import {useAgentNavigation} from './AgentBuilderNav.state';
import {
  AiResearchFieldDefinitionConfig,
  AiResearchFormat,
  AiResearchFormatLabels,
  isAiResearchFieldDefinition,
} from '../../../shared/enrichment';
import {useCustomer} from '../../../hooks/api/metadata';
import {useFieldDefinitionEditorStore} from './AiFieldDefinitionEditor.state';
import {
  useAgentBuilderStore,
  useAgentBuilderServerState,
} from './AgentBuilder.state';
import {BuilderFooter} from './BuilderFooter';
import {ConfigFormLabel} from './ConfigFormLabel';
import {AgentBuilderPreviewButton} from './AgentBuilderPreviewButton';
import {useIsEnrichingAgent} from '../../../hooks/api/enrichment';
import {StepContainer} from './StepContainer';
import {getAiFieldModel} from '../../../shared/agent';
import {AnalysisFieldsEditor} from './AnalysisFieldsEditor';
import {BuilderPreviewCardList} from './BuilderPreviewCardList';

const defaultConfig = {
  analysisFields: [],
  model: getAiFieldModel(),
  fieldType: 'research' as const,
  question: '',
  search: {
    query: '',
    disabled: false,
  },
  format: {
    type: 'structured' as const,
  },
};

const researchFormatOptions = Object.entries(AiResearchFormatLabels).map(
  ([type, label]) => (
    <option key={type} value={type}>
      {label}
    </option>
  )
);

const ResearchFieldEditor = () => {
  const {id: agentId, status} = useAgentBuilderServerState();
  const {data: isEnrichingAgent} = useIsEnrichingAgent({agentId});
  const isDisabled = status === 'published' || isEnrichingAgent;
  const {config, setConfig} = useFieldDefinitionEditorStore();

  if (config && config.fieldType !== 'research') {
    return <>Field type is not research</>;
  }

  const updateConfig = (updates: Partial<AiResearchFieldDefinitionConfig>) => {
    setConfig({
      ...(config ?? defaultConfig),
      ...updates,
    });
  };

  return (
    <Flex direction="column" gap="6">
      <Text fontSize="xl">Prompt your Researcher</Text>

      <FormControl isRequired>
        <ConfigFormLabel>Task</ConfigFormLabel>
        <Textarea
          isDisabled={isDisabled}
          onChange={(e) => updateConfig({question: e.target.value})}
          rows={5}
          placeholder="Ask your AI researcher anything..."
          value={config?.question ?? ''}
        />
        <FormHelperText textColor="kgray.300">
          Prompting is most successful when you phrase your prompt as a question
          about a single account, like &quot;Does this company have brick and
          mortar locations?&quot;
        </FormHelperText>
      </FormControl>

      <FormControl>
        <Flex alignItems="center" justifyContent="space-between">
          <ConfigFormLabel>Search Query</ConfigFormLabel>
          <Switch
            isDisabled={isDisabled}
            isChecked={!config?.search.disabled}
            onChange={(e) => {
              updateConfig({
                search: {
                  ...(config?.search ?? {query: ''}),
                  disabled: !e.target.checked,
                },
              });
            }}
          />
        </Flex>
        <Textarea
          isDisabled={isDisabled || config?.search.disabled}
          onChange={(e) => {
            updateConfig({
              search: {
                ...(config?.search ?? {disabled: false}),
                query: e.target.value,
              },
            });
          }}
          mt="2"
          rows={3}
          placeholder="Enter search query..."
          value={config?.search.query ?? ''}
        />
        <FormHelperText textColor="kgray.300">
          The search query will be used to gather information before answering
          the research question.
        </FormHelperText>
      </FormControl>

      <FormControl>
        <ConfigFormLabel>Format</ConfigFormLabel>
        <Select
          value={config?.format.type ?? 'structured'}
          isDisabled={isDisabled}
          onChange={(e) => {
            const formatType = e.target.value as AiResearchFormat['type'];
            updateConfig({
              format:
                formatType === 'custom'
                  ? {
                      type: 'custom',
                      instructions: '',
                    }
                  : {
                      type: formatType,
                    },
            });
          }}
        >
          {researchFormatOptions}
        </Select>
      </FormControl>

      {config?.format.type === 'custom' && (
        <FormControl>
          <ConfigFormLabel>Custom Instructions</ConfigFormLabel>
          <Textarea
            isDisabled={isDisabled}
            onChange={(e) => {
              if (config?.format.type !== 'custom') return;

              updateConfig({
                format: {
                  type: 'custom',
                  instructions: e.target.value,
                },
              });
            }}
            rows={5}
            placeholder="Enter custom instructions for the research format..."
            value={
              config?.format.type === 'custom' ? config.format.instructions : ''
            }
          />
          <FormHelperText textColor="kgray.300">
            Provide specific instructions on how the research should be
            formatted and presented.
          </FormHelperText>
        </FormControl>
      )}

      <AnalysisFieldsEditor
        config={config ?? defaultConfig}
        isDisabled={isDisabled}
        onConfigChange={setConfig}
      />
    </Flex>
  );
};

export const AgentBuilderEditResearchFieldStep = () => {
  const {
    id: agentId,
    type: agentType,
    status: agentStatus,
  } = useAgentBuilderServerState();
  const {previewAccountIds} = useAgentBuilderStore();
  const {isSaveable, updateServerState, publishChanges} =
    useFieldDefinitionEditorStore();

  const fieldDefinitions = useCustomer().fieldDefinitions ?? [];
  const agentResearchField = fieldDefinitions
    .filter(isAiResearchFieldDefinition)
    .find((fieldDefinition) => fieldDefinition.agentId?.equals(agentId));

  useEffect(() => {
    if (agentResearchField) {
      updateServerState({
        serverState: agentResearchField,
        updateMode: 'ifNoChanges',
      });
    }
  }, [agentResearchField, updateServerState]);

  const onNavigate = useAgentNavigation({
    mutation: publishChanges,
    saveOnBack: agentStatus === 'draft' && isSaveable(),
  });

  return (
    <StepContainer isSaveable={isSaveable()} onSaveMutation={publishChanges}>
      <TwoColumnGrid>
        <Flex
          borderRight="1px solid"
          borderColor="kgray.200"
          direction="column"
        >
          <Box position="relative" h="100%">
            <WrapperFlex>
              <ScrollableFlex p="0">
                <LeftColumnContent>
                  <AgentBuilderPageIndicator />
                  <ResearchFieldEditor />
                </LeftColumnContent>
              </ScrollableFlex>
            </WrapperFlex>
          </Box>

          <Divider />

          <BuilderFooter justifyContent="space-between">
            <Button
              isLoading={publishChanges.isPending}
              onClick={() => onNavigate('prev')}
              variant="ghost"
            >
              Back
            </Button>

            <Button
              colorScheme="kbuttonblue"
              isDisabled={!isSaveable()}
              isLoading={publishChanges.isPending}
              onClick={() => onNavigate('next')}
            >
              {agentType === 'research'
                ? 'Continue to Review'
                : 'Continue to Analyst'}
            </Button>
          </BuilderFooter>
        </Flex>

        <RightColumnContent>
          <Flex alignItems="baseline" justifyContent="space-between" mt="-6px">
            <Text fontSize="xl">Preview</Text>
            <AgentBuilderPreviewButton accountIds={previewAccountIds} />
          </Flex>

          <Box flex="1" position="relative">
            <WrapperFlex>
              <ScrollableFlex p="0">
                <BuilderPreviewCardList
                  accountIds={previewAccountIds}
                  fieldDefinition={agentResearchField}
                  isPreviewDisabled={!isSaveable()}
                />
              </ScrollableFlex>
            </WrapperFlex>
          </Box>
        </RightColumnContent>
      </TwoColumnGrid>
    </StepContainer>
  );
};
